import { Box, Typography, Switch, Divider, TextField } from "@mui/material";

export const TemplateSection = ({
  icon,
  color = "blue",
  title,
  subtitle,
  selected,
  templateName,
  onSwitch,
  onNameChange,
  children,
  highlighted = false,
}) => {
  /* ==========================================
     Tailwind-like color themes
  ========================================== */
  const colors = {
    green: {
      main: "#16a34a", // green-600
      dark: "#15803d", // green-700
      light: "#f0fdf4", // green-50
      soft: "#dcfce7", // green-100
      border: "#bbf7d0", // green-200
      ring: "rgba(22,163,74,.10)",
    },

    blue: {
      main: "#2563eb", // blue-600
      dark: "#1d4ed8", // blue-700
      light: "#eff6ff", // blue-50
      soft: "#dbeafe", // blue-100
      border: "#bfdbfe", // blue-200
      ring: "rgba(37,99,235,.10)",
    },

    orange: {
      main: "#ea580c", // orange-600
      dark: "#c2410c", // orange-700
      light: "#fff7ed", // orange-50
      soft: "#ffedd5", // orange-100
      border: "#fed7aa", // orange-200
      ring: "rgba(234,88,12,.10)",
    },

    yellow: {
      main: "#ca8a04", // yellow-600
      dark: "#a16207", // yellow-700
      light: "#fefce8", // yellow-50
      soft: "#fef9c3", // yellow-100
      border: "#fef08a", // yellow-200
      ring: "rgba(202,138,4,.10)",
    },
  };

  const theme = colors[color] || colors.blue;

  return (
    <Box
      sx={{
        bgcolor: "#fff",

        border: selected ? `1px solid ${theme.border}` : "1px solid #e2e8f0",

        borderRadius: "11px",
        overflow: "hidden",

        boxShadow: selected
          ? `0 0 0 3px ${theme.ring}`
          : "0 1px 2px rgba(15,23,42,.03)",

        transition: "all .15s ease",
      }}
    >
      {/* ==========================================
          Header
      ========================================== */}
      <Box
        sx={{
          px: 1.5,
          py: 1.25,

          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",

          gap: 2,

          bgcolor: selected ? theme.light : highlighted ? "#fafcff" : "#fff",

          transition: "background .15s ease",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.1,
            minWidth: 0,
          }}
        >
          {/* Icon */}
          <Box
            sx={{
              width: 32,
              height: 32,
              flexShrink: 0,

              borderRadius: "8px",

              bgcolor: selected ? theme.main : theme.light,

              color: selected ? "#fff" : theme.main,

              border: selected
                ? "1px solid transparent"
                : `1px solid ${theme.border}`,

              display: "flex",
              alignItems: "center",
              justifyContent: "center",

              boxShadow: selected ? `0 3px 8px ${theme.ring}` : "none",

              transition: "all .15s ease",

              "& svg": {
                fontSize: ".95rem",
              },
            }}
          >
            {icon}
          </Box>

          {/* Title */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: ".8rem",
                fontWeight: 700,
                color: "#0f172a",
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                fontSize: ".65rem",
                color: "#94a3b8",
                mt: 0.1,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>

        {/* Save Template Switch */}
        <Box
          onClick={() => onSwitch(!selected)}
          sx={{
            minWidth: 112,
            px: 1.25,
            py: 0.8,
            flexShrink: 0,

            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,

            borderRadius: "10px",

            bgcolor: selected ? theme.light : "#f8fafc",

            border: selected
              ? `1px solid ${theme.border}`
              : "1px solid #e2e8f0",

            cursor: "pointer",

            transition: "all .18s ease",

            boxShadow: selected
              ? `0 2px 8px ${theme.ring}`
              : "0 1px 2px rgba(15,23,42,.03)",

            "&:hover": {
              bgcolor: selected ? theme.soft : "#f1f5f9",
              borderColor: selected ? theme.main : "#cbd5e1",
              transform: "translateY(-1px)",
            },
          }}
        >
          {/* Label */}
          <Box>
            <Typography
              sx={{
                fontSize: ".62rem",
                fontWeight: 700,
                color: selected ? theme.dark : "#64748b",
                lineHeight: 1.2,
                whiteSpace: "nowrap",
              }}
            >
              Save Template
            </Typography>

            <Typography
              sx={{
                fontSize: ".56rem",
                fontWeight: 500,
                color: selected ? theme.main : "#94a3b8",
                mt: 0.25,
              }}
            >
              {selected ? "Selected" : "Optional"}
            </Typography>
          </Box>

          {/* Compact Switch */}
          <Switch
            size="small"
            checked={selected}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onSwitch(e.target.checked)}
            sx={{
              width: 34,
              height: 20,
              p: 0,

              "& .MuiSwitch-switchBase": {
                p: "2px",

                "&.Mui-checked": {
                  transform: "translateX(14px)",
                  color: "#fff",

                  "& + .MuiSwitch-track": {
                    bgcolor: theme.main,
                    opacity: 1,
                  },
                },
              },

              "& .MuiSwitch-thumb": {
                width: 16,
                height: 16,
                boxShadow: "0 1px 3px rgba(15,23,42,.25)",
              },

              "& .MuiSwitch-track": {
                borderRadius: "20px",
                bgcolor: "#cbd5e1",
                opacity: 1,
              },
            }}
          />
        </Box>
      </Box>

      <Divider />

      {/* ==========================================
          Values
      ========================================== */}
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
        }}
      >
        {children}
      </Box>

      {/* ==========================================
          Template Name
      ========================================== */}
      {selected && (
        <Box
          sx={{
            px: 1.5,
            py: 1.25,

            bgcolor: theme.light,

            borderTop: `1px solid ${theme.border}`,
          }}
        >
          <Typography
            sx={{
              fontSize: ".65rem",
              fontWeight: 600,
              color: theme.dark,
              mb: 0.6,
            }}
          >
            Template Name
          </Typography>

          <TextField
            fullWidth
            autoFocus
            size="small"
            value={templateName}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder={`Enter ${title.toLowerCase()} template name`}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                bgcolor: "#fff",
                fontSize: ".8rem",

                "& fieldset": {
                  borderColor: theme.border,
                },

                "&:hover fieldset": {
                  borderColor: theme.main,
                },

                "&.Mui-focused fieldset": {
                  borderColor: theme.main,
                  borderWidth: "1.5px",
                },

                "&.Mui-focused": {
                  boxShadow: `0 0 0 3px ${theme.ring}`,
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
